import { AxiosError } from 'axios';
import { findNearest } from 'geolib';
import i18n, { t } from 'i18next';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';
import { v4 as uuidv4 } from 'uuid';

import { GeocoderRequests } from '~/api/Geocoder';
import { KYCStatus } from '~/api/KYCStatus';
import type { CheckPromocodeResponse } from '~/api/Order';
import { ReferralProgramRequests } from '~/api/ReferralProgram';
import { MIN_AGE } from '~/components/ModalBirthDate/constants';
import { getAgeByDate } from '~/components/ModalBirthDate/utils';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import * as Sentry from '~/services/sentry';
import {
  DOMAIN,
  REASON,
  reportError,
  reportWarning,
} from '~/services/telemetry';
import {
  cartStore,
  catalogStore,
  checkoutStore,
  mainStore,
  membershipCardChangeStore,
  membershipManageStore,
  membershipPurchaseStore,
  membershipStore,
  orderStore,
} from '~/stores';
import type { SettingsStore } from '~/stores/Settings';
import { MetaStore } from '~/stores/shared/MetaStore';
import { getCookieLang } from '~/utils/cookies';
import { partnerPickupAddressRequest } from '~/utils/partnerPickupAddress';
import { clearStoredPromocode } from '~/utils/referralLink';

import {
  type BonusesTransaction,
  type CheckDokobitStatusParams,
  type Customer,
  type CustomerRequestCodeParams,
  CustomerRequests,
  type DokobitRequestCodeResponse,
  type RequestCodeResponse,
  type SubscriptionFlag,
  type UpdatePersonalFields,
  type VerificationCodeRequestParams,
  VerificationType,
  type VerifyCodeResponse,
} from '../api/Customer';
import {
  type AddAddressRequest,
  type Address,
  type AddressResponse,
  type AddressUsage,
  type AutocompleteRequest,
  type AutocompleteResponse,
  DeliveryRequests,
  type DeliveryZoneCoverage,
  type UpdateAddressRequest,
} from '../api/DeliveryAddress';
import {
  type AddressComponent,
  type AddressComponentType,
  GoogleMapGeocode,
} from '../api/GoogleMapGeocode';
import { type ApiErrorResponse, refreshTokens } from '../api/Requests';
import type { SignInMethod } from '../api/refreshJournal';
import { PERSISTED_USER_STORE_KEY, tokenStorage } from '../api/tokenStorage';
import { company } from '../company/Company';
import { favoritesStore } from './FavoritesStore/FavoritesStore';
import { storage as localStorage } from './LocalStorage';
import { LoyaltyPointsStore } from './LoyaltyPointsStore';
import { MindboxLoyaltyStore } from './MindboxLoyaltyStore';

type AddressComponents = Partial<
  Record<AddressComponentType, Omit<AddressComponent, 'types'>>
>;

export type DeliveryAddress = {
  zip: string;
  country: string;
  city: string;
  region: string;
  address1: string;
  address2: string;
  addressFloor: string;
  addressBld: string;
  addressApt: string;
  shortAddress: string;
  coordinates: GeoCoordinates;
  placeId: string;
  addressId: string | null;
  comment: string;
  instructions: string[];
  type: string;
  /** UUID of the external pickup point (ПВЗ) when the address is a pickup location. */
  pickupPointId?: string | null;
  /**
   * `external_code` of the partner (EMU) pickup point the order goes to. Set
   * only by the partner-point flow, so any other way of picking an address
   * leaves it absent and the order is not sent as a partner pickup.
   */
  partnerPupExternalId?: string | null;
  usage?: AddressUsage;
  warehouseCode?: string;
};

export type DraftUserData = {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
};

export type PersonalData = {
  id: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  phone: string | null;
  flat: string;
  dateOfBirth: string | null;
  email: string | null;
  lastLogin: string | null;
  subscriptionFlags: Record<string, SubscriptionFlag>;
  subscriptionId: string;
  isAdult: boolean;
  isRateApp: boolean;
  type: 'User' | 'Staff';
  freeDeliveryDaysLeft: number;
  kycStatus: KYCStatus;
  verificationStatus: string | null;
  identificationNumber: string | null;
};

type BonusesTransactionsPagination = {
  page: number;
  total: number;
};

interface GeocodingOptions {
  /**
   * Building number to use when the geocoder returns none — PUDO points carry
   * no house number, so callers pass a label like "ПВЗ" to keep the address
   * valid.
   */
  buildingFallback?: string;
}

interface ReverseGeocodeResponse {
  Match_addr: string;
  LongLabel: string;
  ShortLabel: string;
  Addr_type: string;
  Type: string;
  PlaceName: string;
  AddNum: string;
  Address: string;
  Block: string;
  Sector: string;
  Neighborhood: string;
  District: string;
  City: string;
  MetroArea: string;
  Subregion: string;
  Region: string;
  RegionAbbr: string;
  Territory: string;
  Postal: string;
  PostalExt: string;
  CntryName: string;
  CountryCode: string;
}

const isDefinedString = (value: string | undefined): value is string =>
  typeof value === 'string';

const isSavedAfter = (time: string | null, other: string | null) =>
  time !== null && (other === null || time > other);

export class UserStore {
  isFirstLaunch = true;
  isAuthorized = false;
  deliveryAddress: DeliveryAddress | null = null;
  token = '';
  refreshToken = '';
  tokensSavedAt: string | null = null;

  /**
   * Customer whose basket was kept when their session was taken away.
   *
   * A forced logout leaves the basket behind so a re-login does not cost the
   * customer their shopping — but the basket is then unowned, and on a shared
   * device the next person to sign in would inherit it. Recording who it
   * belonged to makes the difference between giving it back and handing it
   * over. Null whenever nothing is being held, which includes every logout the
   * customer asked for, because that one empties the basket outright.
   */
  retainedCartOwnerId: string | null = null;
  lang = getCookieLang();
  dir: 'ltr' | 'rtl' = 'ltr';

  // A unique token to manage address search sessions.
  // Reset the token after an address has been found.
  addressSearchSessionToken?: string;

  loyaltyPointsStore: LoyaltyPointsStore;
  mindboxLoyaltyStore: MindboxLoyaltyStore;

  draftUserData: DraftUserData | null = null;

  isPersonalDataLoading = false;
  personalData: PersonalData = {
    id: null,
    firstName: null,
    middleName: null,
    lastName: null,
    phone: null,
    flat: '',
    dateOfBirth: null,
    email: null,
    lastLogin: null,
    subscriptionFlags: {},
    subscriptionId: '',
    isAdult: false,
    isRateApp: false,
    type: 'User',
    freeDeliveryDaysLeft: 0,
    kycStatus: KYCStatus.NotVerified,
    verificationStatus: null,
    identificationNumber: null,
  };
  deviceId = '';
  playerId = '';
  sessionList: string[] = [];
  addressList: Address[] = [];
  bonuses: number | null = null;
  bonusesTransactionsList: BonusesTransaction[] = [];
  bonusesTransactionsPagination: BonusesTransactionsPagination = {
    total: 0,
    page: 1,
  };
  addressToDelete: string | null = null;
  refereeInfo: CheckPromocodeResponse | null = null;
  referralInfo: CheckPromocodeResponse | null = null;
  deliveryZonesCoverages: DeliveryZoneCoverage[] = [];
  deliveryZoneCacheTime: number = 0;

  readonly emailChangeMeta: MetaStore = new MetaStore();
  readonly deliveryAddressMeta: MetaStore = new MetaStore();

  constructor(protected readonly settingStore: SettingsStore) {
    this.loyaltyPointsStore = new LoyaltyPointsStore(this);
    this.mindboxLoyaltyStore = new MindboxLoyaltyStore(this);

    makeAutoObservable(this);
    makePersistable(this, {
      name: PERSISTED_USER_STORE_KEY,
      properties: [
        'isFirstLaunch',
        'isAuthorized',
        'deliveryAddress',
        'addressList',
        'token',
        'refreshToken',
        'tokensSavedAt',
        'retainedCartOwnerId',
        'personalData',
        'draftUserData',
        'deviceId',
        'playerId',
        'sessionList',
        'lang',
      ],
      storage: localStorage,
    })
      .then(() => {
        this.dropUnusableDeliveryAddress();
        refreshTokens.sync('hydration');
      })
      .catch((error) => error && console.error(error));
  }

  /**
   * Drops a restored delivery address that carries no coordinates.
   *
   * Runs on the hydration promise rather than from a component: hydration is
   * async, so a synchronous check races it and either judges an address that
   * is not restored yet or never re-checks the one that arrives afterwards.
   * Storage that fails to hydrate needs no check — nothing was restored.
   *
   * Anything written later goes through `setDeliveryAddress`, which refuses
   * the same shape outright.
   */
  private dropUnusableDeliveryAddress(): void {
    const { deliveryAddress } = this;
    if (!deliveryAddress || deliveryAddress.coordinates) {
      return;
    }

    this.setDeliveryAddress(null);
  }

  /**
   * Reports an address that had to be discarded for having no coordinates.
   *
   * It is otherwise invisible: the address vanishes from a live session and
   * the checkout gate then opens the address modal forever, with no trace of
   * where the record came from.
   */
  private reportUnusableDeliveryAddress(address: DeliveryAddress): void {
    reportWarning(
      DOMAIN.address,
      'Delivery address discarded: no coordinates',
      {
        reason: REASON.address.discardedNoCoordinates,
        extras: {
          addressId: address.addressId ?? null,
          presentFields: Object.keys(address),
          isAuthorized: this.isAuthorized,
          addressListLength: this.addressList.length,
        },
      },
    );
  }

  // Getters
  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  get fullName(): string {
    return `${this.personalData.firstName || ''} ${
      this.personalData.middleName || ''
    } ${this.personalData.lastName || ''}`
      .trim()
      .replace('  ', ' ');
  }

  get email(): string {
    return this.personalData.email || '';
  }

  get phone() {
    return this.personalData.phone || '';
  }

  get isStaff(): boolean {
    return this.personalData.type === 'Staff';
  }

  get verificationStatus(): string | null {
    return this.personalData.verificationStatus;
  }

  get identificationNumber(): string | null {
    return this.personalData.identificationNumber;
  }

  get dateOfBirth(): string | null {
    const { dateOfBirth } = this.personalData;

    if (!dateOfBirth) {
      return null;
    }

    const date = new Date(dateOfBirth);
    const monthSource = date.getMonth() + 1;
    const month = monthSource > 9 ? monthSource : `0${monthSource}`;
    const day = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
    return `${date.getFullYear()}-${month}-${day}`;
  }

  get age(): number | null {
    if (!this.personalData.dateOfBirth) {
      return null;
    }

    return getAgeByDate(new Date(this.personalData.dateOfBirth));
  }

  get formattedDateOfBirth(): string | null {
    if (!this.personalData.dateOfBirth) {
      return null;
    }

    try {
      const date = new Date(this.personalData.dateOfBirth);
      return date.toLocaleDateString();
    } catch {
      return this.personalData.dateOfBirth;
    }
  }

  // Setters
  setIsFirstLaunch(flag: boolean) {
    this.isFirstLaunch = flag;
  }

  /**
   * The active delivery address, or `null`.
   *
   * A record without `coordinates` is refused outright rather than stored:
   * everything downstream — the ETA, the cart calculation, the checkout —
   * reads them, most of it as `deliveryAddress?.coordinates.lat`, which guards
   * the address and not the field. Keeping one would either throw at those
   * call sites or, worse, be quietly priced against the company's default
   * warehouse. This is the invariant; the hydration check only covers the one
   * path that predates it.
   */
  setDeliveryAddress(deliveryAddress: DeliveryAddress | null) {
    if (deliveryAddress && !deliveryAddress.coordinates) {
      this.reportUnusableDeliveryAddress(deliveryAddress);
      this.deliveryAddress = null;
      return;
    }

    this.deliveryAddress = deliveryAddress;
  }

  setDeliveryAddress1(val: string) {
    if (!this.deliveryAddress) {
      return;
    }
    this.deliveryAddress.address1 = val;
    this.deliveryAddress.shortAddress = `${this.deliveryAddress.zip}, ${val}`;
  }

  setDeliveryAddress2(val: string) {
    if (!this.deliveryAddress) {
      return;
    }
    this.deliveryAddress.address2 = val;
  }

  setDeliveryAddressFloor(val: string) {
    if (!this.deliveryAddress) {
      return;
    }
    this.deliveryAddress.addressFloor = val;
  }

  setDeliveryAddressBld(val: string) {
    if (!this.deliveryAddress) {
      return;
    }
    this.deliveryAddress.addressBld = val;
  }

  setDeliveryAddressApt(val: string) {
    if (!this.deliveryAddress) {
      return;
    }
    this.deliveryAddress.addressApt = val;
  }

  setDraftUserData(data: DraftUserData | null) {
    this.draftUserData = data;
  }

  setPersonalDataName(val: string) {
    this.personalData = { ...this.personalData, ...this.splitFullName(val) };
  }

  setPersonalData(data: Omit<Customer, 'app_rating' | 'type'>) {
    if (!data.id) {
      return;
    }
    this.personalData.id = data.id;
    this.personalData.firstName =
      data.first_name || this.draftUserData?.firstName || '';
    this.personalData.middleName =
      data.middle_name || this.draftUserData?.middleName || '';
    this.personalData.lastName =
      data.last_name || this.draftUserData?.lastName || '';
    this.personalData.dateOfBirth = data.date_of_birth;
    this.personalData.email = data.email;
    this.personalData.lastLogin = data.last_login;
    this.personalData.isAdult = data.age_restriction ?? false;
    this.personalData.type = data.customer_type?.code || 'User';
    this.personalData.freeDeliveryDaysLeft = data.freeDeliveryDaysLeft || 0;
    const previousKycStatus = this.personalData.kycStatus;
    this.personalData.kycStatus = data.kyc_verification_status;

    if (
      data.kyc_verification_status &&
      data.kyc_verification_status !== previousKycStatus
    ) {
      analyticsService.track(AnalyticsEvent.KYC_VERIFICATION_STATUS, {
        status: data.kyc_verification_status,
      });
    }
    this.personalData.verificationStatus = data.verification_status || null;
    this.personalData.identificationNumber = data.identification_number || null;
    if (this.personalData.subscriptionId && data.subscription?.length) {
      for (let i = 0; i < data.subscription.length; i++) {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        if (data.subscription[i].id !== this.personalData.subscriptionId) {
          continue;
        }
        for (
          let j = 0;
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          j < data.subscription[i].subscription_flags.length;
          j++
        ) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          this.personalData.subscriptionFlags[
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            data.subscription[i].subscription_flags[j].name
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          ] = data.subscription[i].subscription_flags[j];
        }
      }
    }
    const fullName = `${data.first_name || ''} ${data.middle_name || ''} ${
      data.last_name || ''
    }`
      .trim()
      .replace('  ', ' ');

    if (data.email) {
      mainStore.sendToRN('setOneSignalUserEmail', data.email);
    }

    mainStore.sendToRN('setUserName', {
      welcomeText: '', //i18n.t('hello'),
      name: fullName,
    });
    mainStore.setSentryUser({
      id: data.id,
      name: fullName,
      email: data.email || '',
    });
    analyticsService.setUserId(data.id);
    mainStore.sendToRN('sendTags', {
      first_name: data.first_name || '',
    });
    mainStore.sendToRN('setOneSignalUserID', {
      id: data.id,
      phone: data.phone || undefined,
    });
  }

  setIsAdult(flag: boolean) {
    this.personalData.isAdult = flag;

    if (!this.personalData.id) {
      return;
    }

    this.updatePersonalData({
      age_restriction: flag,
      email: this.personalData.email || '',
    }).catch((error) => error && console.error(error));
  }

  setIsRateApp(flag: boolean) {
    this.personalData.isRateApp = flag;
    this.updatePersonalData({
      app_rating: flag,
      email: this.personalData.email || '',
    }).catch((error) => error && console.error(error));
  }

  saveTokens(access: string, refresh: string) {
    const savedAt = new Date().toISOString();

    tokenStorage.write({
      access,
      refresh,
      personalData: toJS(this.personalData),
      savedAt,
    });

    this.token = access;
    this.refreshToken = refresh;
    this.tokensSavedAt = savedAt;
  }

  /**
   * Reconciles the tokens held in memory with the persisted UserStore record,
   * keeping whichever was saved last.
   *
   * They can disagree when another tab rotates the pair, or when that tab's
   * persistor overwrites the record with the older pair it still holds. Sending
   * the older refresh token spends a token the backend has already rotated
   * and ends the session.
   *
   * A signed-out record is ambiguous: another tab either signed out, or it
   * was a guest tab whose persistor wrote its own guest state over this
   * session. Only a sign-out stamped after this tab saved its pair means the
   * session ended; its tokens are then revoked, and writing them back would
   * sign a guest in as that customer, so the caller reloads the tab to take
   * over the persisted state instead. Without such a stamp this tab writes its
   * pair back. A record holding another customer is treated as a sign-out too.
   *
   * A guest tab reloads into a session signed in elsewhere after the last
   * sign-out, so that its persistor stops overwriting it with guest state.
   */
  syncTokens(): 'store' | 'storage' | 'signedOut' | 'signedIn' | null {
    const stored = tokenStorage.read();

    if (!stored) {
      return null;
    }

    const signedOutAt = tokenStorage.signedOutAt();

    if (!this.isAuthorized || !this.refreshToken) {
      return stored.status === 'signedIn' &&
        isSavedAfter(stored.savedAt, signedOutAt)
        ? 'signedIn'
        : null;
    }

    if (stored.status === 'signedOut') {
      return isSavedAfter(signedOutAt, this.tokensSavedAt)
        ? 'signedOut'
        : this.persistTokens();
    }

    if (stored.customerId !== (this.personalData.id ?? null)) {
      return 'signedOut';
    }

    if (stored.refresh === this.refreshToken) {
      return this.tokensSavedAt ? null : this.persistTokens();
    }

    if (isSavedAfter(stored.savedAt, this.tokensSavedAt)) {
      this.token = stored.access;
      this.refreshToken = stored.refresh;
      this.tokensSavedAt = stored.savedAt;
      mainStore.sendToRN('setToken', stored.access);

      return 'storage';
    }

    return this.persistTokens();
  }

  private persistTokens(): 'store' {
    const savedAt = this.tokensSavedAt ?? new Date().toISOString();

    tokenStorage.write({
      access: this.token,
      refresh: this.refreshToken,
      personalData: toJS(this.personalData),
      savedAt,
    });
    this.tokensSavedAt = savedAt;

    return 'store';
  }

  // Resets the addressSearchSessionToken with a new UUID.
  // Call this method after an address has been successfully found.
  resetAddressSearchSessionToken() {
    this.addressSearchSessionToken = uuidv4();
  }

  /**
   * Records the device id the native app supplied, or the web's placeholder.
   *
   * A missing id no longer ends the session. `device_id` is only sent while
   * authorising — requesting and verifying an OTP — so a session already
   * holding a token needs nothing from it, while the app can supply one late,
   * empty, or never. Logging the customer out here cost them the token, the
   * address list persisted beside it and the trip they were in the middle of,
   * over a value nothing was about to ask for.
   */
  setDeviceId(val: string) {
    this.deviceId = val;
    if (!val || val === '00000000-0000-0000-0000-000000000000') {
      // Only from the app. The web deliberately assigns the all-zero id to
      // every session (`useRequestCycle`), so reporting it there described the
      // app working as designed — which is most of what the 14,960 events
      // against 10,021 users in six days actually were. In the native app the
      // same value means the handshake failed to supply one, which is a real
      // fault. Once per session either way: the id does not change within one.
      if (mainStore.isRN) {
        reportWarning(DOMAIN.rn, 'Received invalid deviceID', {
          reason: REASON.rn.invalidDeviceId,
          once: true,
          extras: { deviceId: val },
        });
      }
    }
  }

  setPlayerId(val: string) {
    this.playerId = val;
  }

  setSubscriptionFlags(flags: Record<string, SubscriptionFlag>) {
    this.personalData.subscriptionFlags = flags;
  }

  setAddressList(addressList: Address[]) {
    this.addressList = addressList;
  }

  resetCustomerType() {
    this.personalData.type = 'User';
    this.personalData.freeDeliveryDaysLeft = 0;
    orderStore.setIsFirstOrder(true);
  }

  setBonuses(bonuses: number | null) {
    this.bonuses = bonuses;
  }

  setBonusesTransactionsList(list: BonusesTransaction[]) {
    if (!list.length) {
      this.bonusesTransactionsList = [];
    } else {
      this.bonusesTransactionsList.push(...list);
    }
  }

  setBonusesTransactionsPagination(val: BonusesTransactionsPagination) {
    this.bonusesTransactionsPagination = val;
  }

  setAddressToDelete(id: string | null) {
    this.addressToDelete = id;
  }

  setLang(val: string) {
    this.lang = val;
  }

  setDir(val: 'ltr' | 'rtl') {
    this.dir = val;
  }

  // Actions
  getShortAddress(addressComponents: AddressComponents): string {
    let address = '';
    if (addressComponents.street_number && addressComponents.route) {
      address += addressComponents.street_number.long_name;

      if (addressComponents.route) {
        if (address) {
          address += ' ';
        }
        address += addressComponents.route.long_name;
      }
      if (addressComponents.administrative_area_level_1) {
        if (address) {
          address += ', ';
        }
        address += addressComponents.administrative_area_level_1.long_name;
      }
      if (addressComponents.locality) {
        if (address) {
          address += ', ';
        }
        address += addressComponents.locality.long_name;
      }
      if (addressComponents.country) {
        if (address) {
          address += ', ';
        }
        address += addressComponents.country.long_name;
      }
    } else if (
      addressComponents.premise ||
      addressComponents.route ||
      addressComponents.neighborhood
    ) {
      if (addressComponents.premise) {
        address += addressComponents.premise.long_name;
      }
      if (addressComponents.route) {
        if (address) {
          address += ', ';
        }
        address += addressComponents.route.long_name;
      }
      if (addressComponents.neighborhood) {
        if (address) {
          address += ', ';
        }
        address += addressComponents.neighborhood.long_name;
      }
    } else {
      if (addressComponents.locality) {
        address += addressComponents.locality.long_name;
      }
      if (addressComponents.administrative_area_level_1) {
        if (address) {
          address += ', ';
        }
        address += addressComponents.administrative_area_level_1.long_name;
      }
      if (addressComponents.country) {
        if (address) {
          address += ', ';
        }
        address += addressComponents.country.long_name;
      }
    }
    return address;
  }

  splitFullName(fullName: string): {
    firstName: string;
    middleName: string;
    lastName: string;
  } {
    let firstName = '';
    let middleName = '';
    let lastName = '';
    if (!fullName) {
      return { firstName, middleName, lastName };
    }
    let nameParts = fullName.replace(/\s+/g, ' ').split(' ');
    nameParts = nameParts.filter((item) => item.length);
    if (nameParts.length === 1) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      firstName = nameParts[0];
      middleName = '';
      lastName = '';
    } else if (nameParts.length === 2) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      firstName = nameParts[0];
      middleName = '';
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      lastName = nameParts[1];
    } else if (nameParts.length >= 3) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      firstName = nameParts[0];
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      middleName = nameParts[1];
      lastName = nameParts.slice(2).join(' ').trim();
    }
    return { firstName, middleName, lastName };
  }

  sendSubscriptionAnalytics(
    subscriptionFlags: SubscriptionFlag[],
    source: string,
  ) {
    const sendAnalytics = ({
      name,
      push = false,
      email = '',
    }: SubscriptionFlag) => {
      analyticsService.track(AnalyticsEvent.SUBSCRIBE_CHANGE_SETTINGS, {
        type: 'push',
        kind: name,
        toggle: push ? 'on' : 'off',
        source,
      });
      analyticsService.track(AnalyticsEvent.SUBSCRIBE_CHANGE_SETTINGS, {
        type: 'email',
        kind: name,
        toggle: email ? 'on' : 'off',
        source,
      });
    };
    const tagData: {
      subscribed_personal_offers?: string;
      subscribed_discounts?: string;
      subscribed_thirdparty?: string;
    } = {};
    for (let i = 0; i < subscriptionFlags.length; i++) {
      let flag = '';
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      const { name, push = false, email = false } = subscriptionFlags[i];
      if (push && email) {
        flag = 'all';
      } else if (!push && !email) {
        flag = '0';
      } else {
        flag = push ? 'push' : 'email';
      }
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      sendAnalytics(subscriptionFlags[i]);
      if (name === 'personalised') {
        tagData['subscribed_personal_offers'] = flag;
        mainStore.sendToRN('setUserProperties', {
          'Subscription: personalized email': email,
        });
        mainStore.sendToRN('setUserProperties', {
          'Subscription: personalized push': push,
        });
      }
      if (name === 'discounts') {
        tagData['subscribed_discounts'] = flag;
        mainStore.sendToRN('setUserProperties', {
          'Subscription: discounts email': email,
        });
        mainStore.sendToRN('setUserProperties', {
          'Subscription: discounts push': push,
        });
      }
      if (name === 'third_party') {
        tagData['subscribed_thirdparty'] = flag;
        mainStore.sendToRN('setUserProperties', {
          'Subscription: 3d party email': email,
        });
        mainStore.sendToRN('setUserProperties', {
          'Subscription: 3d party push': push,
        });
      }
    }
    if (Object.keys(tagData).length) {
      mainStore.sendToRN('sendTags', tagData);
    }
  }

  // Requests API
  requestAuthCode(
    phone: string,
    captchaToken: string,
    options: {
      useDokobit?: boolean;
      type?: VerificationType;
      personalCode?: string;
      offerAccepted?: boolean;
    } = {},
  ): Promise<DokobitRequestCodeResponse | RequestCodeResponse | AxiosError> {
    const {
      useDokobit,
      type = VerificationType.MOBILE,
      personalCode,
      offerAccepted,
    } = options;
    let verificationParams: VerificationCodeRequestParams;

    if (type === VerificationType.AUDKENNI) {
      if (!isDefinedString(personalCode)) {
        return Promise.reject(new Error('Personal code is required'));
      }

      verificationParams = {
        type: VerificationType.AUDKENNI,
        personal_code: personalCode,
      };
    } else {
      verificationParams = {
        type: VerificationType.MOBILE,
      };
    }

    const params: CustomerRequestCodeParams = {
      phone_number: phone.replace('+', ''),
      recaptcha_token: captchaToken,
      device_id: this.deviceId,
      ...verificationParams,
    };

    if (useDokobit) {
      params.useDokobit = true;
    }

    if (company.authAgreementFieldsEnabled) {
      params.language = this.lang;
      params.offer_accepted = offerAccepted ?? false;
    }

    return CustomerRequests[useDokobit ? 'sendDokobitCode' : 'requestCode'](
      params,
    ).catch((e) => {
      if (!useDokobit) {
        return this.errorHandler(e, 'requestAuthCode');
      } else {
        return Promise.reject(e);
      }
    });
  }

  verifyAuthCode(
    phone: string,
    code: string,
    restore: boolean,
    offerAccepted?: boolean,
  ): Promise<
    | (VerifyCodeResponse & {
        previousEmail: string | null;
      })
    | AxiosError
  > {
    const isAdult = this.personalData.isAdult;
    if (mainStore.isRN && !this.deviceId) {
      reportWarning(DOMAIN.rn, 'Empty deviceID used', {
        reason: REASON.rn.emptyDeviceId,
        once: true,
      });
    }

    return CustomerRequests.verifyCode({
      phone_number: phone.replace('+', ''),
      otp: code,
      device_id: this.deviceId,
      ...(restore ? { restore } : {}),
      ...(company.authAgreementFieldsEnabled
        ? { language: this.lang, offer_accepted: offerAccepted ?? false }
        : {}),
    })
      .then((e) => {
        const previousEmail = this.personalData.email;
        runInAction(() => {
          if (e.verified && e.access_token && e.refresh_token) {
            this._authenticateProfile(e, isAdult, 'otp');
          } else {
            //mainStore.pushAlert('error', i18n.t('errors:otpCodeError'));
            analyticsService.track(AnalyticsEvent.LOGIN_CODE_ENTERED, {
              success: false,
            });
            analyticsService.track(AnalyticsEvent.LOGIN_CODE_ENTERED_FIREBASE, {
              success: false,
            });
            mainStore.sendToRN('setUserProperties', {
              'General: logged in': false,
            });
            mainStore.sendToRN('setUserProperties', {
              customerId: null,
            });
          }
        });
        return Promise.resolve({ ...e, previousEmail });
      })
      .catch((e) => {
        analyticsService.track(AnalyticsEvent.LOGIN_CODE_ENTERED, {
          success: false,
        });
        analyticsService.track(AnalyticsEvent.LOGIN_CODE_ENTERED_FIREBASE, {
          success: false,
        });
        return this.errorHandler(e, 'verifyAuthCode');
      });
  }

  private _authenticateProfile(
    e: VerifyCodeResponse,
    prevIsAdult: boolean,
    method: SignInMethod,
  ) {
    // Before the incoming customer overwrites the previous one: a basket held
    // across a forced logout goes back to the customer it belongs to, and to
    // nobody else. Checked here rather than on logout because that is where
    // the answer first exists.
    if (
      this.retainedCartOwnerId &&
      this.retainedCartOwnerId !== e.customer.id
    ) {
      cartStore.cart = [];
    }
    this.retainedCartOwnerId = null;

    this.personalData.id = e.customer.id;
    this.personalData.firstName =
      e.customer.first_name || this.draftUserData?.firstName || '';
    this.personalData.middleName =
      e.customer.middle_name || this.draftUserData?.middleName || '';
    this.personalData.lastName =
      e.customer.last_name || this.draftUserData?.lastName || '';
    this.personalData.phone = e.customer.phone?.replace('+', '') ?? '';
    this.personalData.dateOfBirth = e.customer.date_of_birth;
    this.personalData.email = e.customer.email;
    this.personalData.lastLogin = e.customer.last_login;
    this.personalData.isAdult = e.customer.age_restriction || false;
    this.personalData.isRateApp = e.customer.app_rating || false;
    this.personalData.type = e.customer.type?.code || 'User';
    this.personalData.freeDeliveryDaysLeft =
      e.customer.freeDeliveryDaysLeft || 0;
    this.personalData.kycStatus = e.customer.kyc_verification_status;
    this.saveTokens(e.access_token, e.refresh_token);
    this.isAuthorized = true;
    this.sessionList.push(new Date().toISOString());
    this.mindboxLoyaltyStore.requestBalance();
    if (cartStore.cart.length && e.customer.freeDeliveryDaysLeft > 0) {
      mainStore.setIsFreeDeliveryPopover(true);
    }
    if (catalogStore.favoritesList.length) {
      favoritesStore.addFavorite(
        catalogStore.favoritesList.map((item) => item.id),
      );
    }

    if (
      !e.customer.first_name &&
      !e.customer.email &&
      this.draftUserData?.email
    ) {
      this.updatePersonalData({
        first_name: this.draftUserData.firstName,
        last_name: this.draftUserData.lastName,
        middle_name: this.draftUserData.middleName,
        email: this.draftUserData.email,
      });

      this.setDraftUserData(null);
    }

    if (prevIsAdult && !e.customer.age_restriction) {
      this.updatePersonalData({
        age_restriction: true,
        email: this.personalData.email || '',
      }).catch((error) => error && console.error(error));
    }
    this.setAddressList(e.addresses || []);
    if (this.deliveryAddress?.address1) {
      for (let i = 0; i < e.addresses.length; i++) {
        if (
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          e.addresses[i].street_address_1 === this.deliveryAddress.address1
        ) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          this.deliveryAddress.addressId = e.addresses[i].id;
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          this.deliveryAddress.comment = e.addresses[i].comment || '';
          this.deliveryAddress.instructions =
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            e.addresses[i].instructions || [];
          this.deliveryAddress.address2 =
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            e.addresses[i].street_address_2 || '';
          break;
        }
      }
    }
    if (
      this.deliveryAddress &&
      this.deliveryAddress.address1 &&
      !this.deliveryAddress.addressId
    ) {
      this.addAddress({
        street_address_1: this.deliveryAddress.address1,
        street_address_2: this.deliveryAddress.address2,
        building: this.deliveryAddress.addressBld,
        floor: this.deliveryAddress.addressFloor,
        apartment: this.deliveryAddress.addressApt,
        city: this.deliveryAddress.city,
        postcode: this.deliveryAddress.zip,
        latitude: this.deliveryAddress.coordinates.lat,
        longitude: this.deliveryAddress.coordinates.lng,
        country: this.deliveryAddress.country,
        comment: this.deliveryAddress.comment,
        instructions: this.deliveryAddress.instructions,
        type: this.deliveryAddress.type,
        ...partnerPickupAddressRequest(
          this.deliveryAddress,
          company.partnerPickupDeliveryProvider,
        ),
      })
        .then((e) => {
          runInAction(() => {
            if (!this.deliveryAddress || !e) {
              return;
            }
            this.deliveryAddress.addressId = e?.id;
            this.setDeliveryAddress2(e.street_address_2 ?? '');
          });
        })
        .catch((error) => error && console.error(error));
    }
    const fullName = `${e.customer.first_name || ''} ${
      e.customer.middle_name || ''
    } ${e.customer.last_name || ''}`
      .trim()
      .replace('  ', ' ');
    mainStore.sendToRN('setUserName', {
      welcomeText: '', //i18n.t('hello'),
      name: fullName,
      customerId: e.customer.id,
    });
    mainStore.sendToRN('setToken', e.access_token);
    mainStore.setSentryUser({
      id: e.customer.id,
      name: fullName,
      email: e.customer.email || '',
      phone: `${e.customer.phone}`,
    });
    refreshTokens.signedIn(method);
    analyticsService.setUserId(e.customer.id);
    analyticsService.track(AnalyticsEvent.LOGIN_CODE_ENTERED, {
      success: true,
    });
    analyticsService.track(AnalyticsEvent.LOGIN_CODE_ENTERED_FIREBASE, {
      success: true,
    });
    mainStore.sendToRN('setOneSignalUserID', {
      id: e.customer.id,
      phone: e.customer.phone || undefined,
    });

    orderStore.checkIsFirstOrder(true).then((isFirst) => {
      if (isFirst) {
        analyticsService.track(AnalyticsEvent.SIGN_UP, {
          method: 'SMS',
          customer_id: e.customer.id,
        });
        cartStore.calculateCart();
      }
    });

    mainStore.sendToRN('setUserProperties', {
      'General: logged in': true,
    });
    mainStore.sendToRN('setUserProperties', {
      'General: login date': new Date().toISOString(),
    });
    mainStore.sendToRN('setUserProperties', {
      'General: last session date':
        this.sessionList[this.sessionList.length - 1],
    });
    mainStore.sendToRN('setUserProperties', {
      'General: total sessions': this.sessionList.length,
    });
    mainStore.sendToRN('setUserProperties', {
      'General: user type': 'normal',
    });
    mainStore.sendToRN('setUserProperties', {
      customerId: e.customer.id,
    });
    analyticsService.track(AnalyticsEvent.LOGIN, {
      method: 'SMS',
    });
  }

  async requestPersonalData(avoidCartCalculation = false) {
    if (this.isPersonalDataLoading) {
      return;
    }
    if (!this.personalData.id) {
      return Promise.reject();
    }

    this.isPersonalDataLoading = true;
    try {
      const [data] = await CustomerRequests.getPersonalData(
        this.personalData.id,
      );
      if (!data) {
        return;
      }
      this.setPersonalData(data);
      this.mindboxLoyaltyStore.requestBalance();

      // when user signed up, check for the first order
      if (avoidCartCalculation) {
        return;
      }

      orderStore.checkIsFirstOrder().then((isFirst) => {
        if (isFirst) {
          cartStore.calculateCart();
        }
      });
    } catch (error) {
      this.errorHandler(
        error as AxiosError<ApiErrorResponse>,
        'requestPersonalData',
      );
      return Promise.reject();
    } finally {
      this.isPersonalDataLoading = false;
    }
  }

  async updatePersonalData(data: UpdatePersonalFields) {
    if (!this.personalData.id) {
      return Promise.reject();
    }
    try {
      const { customer: [customerData] = [] } =
        await CustomerRequests.updatePersonalData(this.personalData.id, data);
      if (!customerData) {
        mainStore.pushAlert('error', i18n.t('errors:oops'));
        await this.requestPersonalData();
        return Promise.reject();
      }
      this.setPersonalData(customerData);
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.data.error &&
        error.response.data.message === 'Email in use.'
      ) {
        mainStore.pushAlert('error', i18n.t('errors:emailAlreadyExists'));
      } else if (error instanceof AxiosError && error.response?.data.message) {
        const apiMessageToKey: Record<string, string> = {
          'first_name must be between 2 and 22 characters': 'firstNameInvalid',
          'middle_name must be between 2 and 22 characters':
            'middleNameInvalid',
          'last_name must be between 2 and 22 characters': 'lastNameInvalid',
        };
        const translationKey = apiMessageToKey[error.response.data.message];
        if (translationKey) {
          await this.requestPersonalData();
          return Promise.reject(translationKey);
        }
        mainStore.pushAlert('error', error.response.data.message);
      } else {
        mainStore.pushAlert('error', i18n.t('errors:oops'));
      }
      await this.requestPersonalData();
      return Promise.reject();
    }
  }

  async updatePersonalEmail(email: string): Promise<boolean> {
    if (!this.personalData.id) {
      return false;
    }
    const emailCached = this.personalData.email || '';
    this.emailChangeMeta.setLoading(true);
    try {
      const { customer: [customerData] = [] } =
        await CustomerRequests.updatePersonalData(this.personalData.id, {
          email: email.trim() || null,
        });

      if (!customerData) {
        await CustomerRequests.updatePersonalData(this.personalData.id, {
          email: emailCached || null,
        }).catch((error) => error && console.error(error));
        mainStore.pushAlert('error', i18n.t('errors:oops'));
        return false;
      }
      this.setPersonalData(customerData);
      return true;
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.data.error &&
        error.response.data.message === 'Email in use.'
      ) {
        mainStore.pushAlert('error', i18n.t('errors:emailAlreadyExists'));
        checkoutStore.setEmailVal(emailCached);
      } else {
        mainStore.pushAlert('error', i18n.t('errors:oops'));
      }
      return false;
    } finally {
      this.emailChangeMeta.setLoading(false);
    }
  }

  async updateDeviceData(
    name: string,
    email: string,
    flags: SubscriptionFlag[],
  ): Promise<boolean> {
    if (mainStore.isRN && !this.deviceId) {
      reportWarning(DOMAIN.rn, 'Empty deviceID used', {
        reason: REASON.rn.emptyDeviceId,
        once: true,
      });
      return false;
    }
    try {
      const { device } = await CustomerRequests.updateDeviceData(
        this.deviceId,
        {
          name,
          email: email.trim(),
          subscription_flags: flags,
        },
      );
      runInAction(() => {
        const { firstName, middleName, lastName } = this.splitFullName(
          device.name || '',
        );
        this.personalData.firstName = firstName;
        this.personalData.middleName = middleName;
        this.personalData.lastName = lastName;
        this.personalData.email = device.email;
        if (
          device.subscriptions?.subscription_flags &&
          device.subscriptions?.subscription_flags?.length
        ) {
          for (
            let i = 0;
            i < device.subscriptions.subscription_flags.length;
            i++
          ) {
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            this.personalData.subscriptionFlags[
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              device.subscriptions.subscription_flags[i].name
            ] = device.subscriptions.subscription_flags[i];
          }
        }
        mainStore.sendToRN('setUserName', {
          welcomeText: '', //i18n.t('hello'),
          name: device.name || '',
        });
        mainStore.sendToRN('sendTags', {
          first_name: firstName || '',
        });
      });
      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'updateDeviceData',
      );
    }
    return false;
  }

  async updateSubscription(
    flags: SubscriptionFlag[],
    source: string,
  ): Promise<boolean> {
    if (!this.isAuthorized) {
      return false;
    }
    if (mainStore.isRN && !this.deviceId) {
      reportWarning(DOMAIN.rn, 'Empty deviceID used', {
        reason: REASON.rn.emptyDeviceId,
        once: true,
      });
      return false;
    }
    try {
      const data = await CustomerRequests.updateSubscription(this.deviceId, {
        subscription_flags: flags,
      });
      runInAction(() => {
        if (
          data.subscription.subscription_flags &&
          data.subscription.subscription_flags.length
        ) {
          for (
            let i = 0;
            i < data.subscription.subscription_flags.length;
            i++
          ) {
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            this.personalData.subscriptionFlags[
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              data.subscription.subscription_flags[i].name
            ] = data.subscription.subscription_flags[i];
          }
          this.sendSubscriptionAnalytics(
            data.subscription.subscription_flags,
            source,
          );
        } else {
          this.personalData.subscriptionFlags = {};
        }
        this.personalData.subscriptionId = data.subscription.id;
      });
      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'updateSubscription',
      );
    }
    return false;
  }

  async checkDokobitStatus(data: CheckDokobitStatusParams): Promise<{
    verified: boolean;
    method: 'sms' | undefined;
  }> {
    const { data: response } = await CustomerRequests.checkDokobitStatus({
      ...data,
      device_id: this.deviceId,
    });

    if (
      response?.verified &&
      response?.access_token &&
      response?.refresh_token
    ) {
      const { phone, date_of_birth } = response.customer;

      let isAdult = false;

      if (date_of_birth) {
        const age = getAgeByDate(new Date(date_of_birth));

        isAdult = age >= (this.settingStore.maxMinAge || MIN_AGE);
      }

      if (response.method !== 'sms') {
        this._authenticateProfile(
          {
            ...response,
            customer: {
              ...response.customer,
              phone: phone?.replace('+', '') ?? '',
              age_restriction: isAdult,
            },
          },
          this.personalData.isAdult,
          'dokobit',
        );
      }
    }

    return {
      verified: response !== null,
      method: response?.method,
    };
  }

  requestNewToken() {
    if (!this.isAuthorized || !this.refreshToken) {
      return Promise.reject();
    }

    return CustomerRequests.refreshToken({
      refresh_token: this.refreshToken,
      customer_id: this.personalData.id || '',
    }).then(({ access_token, refresh_token }) => {
      if (!this.isAuthorized) {
        return;
      }

      this.saveTokens(access_token, refresh_token);
      mainStore.sendToRN('setToken', access_token);
    });
  }

  /**
   * Ends the session and returns the app to its signed-out state.
   *
   * `cause` separates the customer asking to leave from the session being taken
   * away underneath them. Both clear everything the account owns, but only the
   * first clears the basket: a forced logout is frequently not the customer's
   * doing at all — a refresh token the backend can no longer find ends the
   * session of someone who was mid-shop — and emptying their basket on top of
   * signing them out turns a re-login into a lost order. A signed-out basket is
   * a state the app already supports: it persists locally, `calculateCart`
   * works without a token, and `_authenticateProfile` already handles signing
   * in with items in it.
   */
  async logout({ cause = 'user' }: { cause?: 'user' | 'sessionExpired' } = {}) {
    if (!this.isAuthorized) {
      return Promise.reject();
    }

    if (cause === 'user') {
      refreshTokens.signedOut();
    }

    try {
      await CustomerRequests.logout({
        refresh_token: this.refreshToken,
        customer_id: this.personalData.id || '',
      });
    } catch (error) {
      console.error(error);
    }
    const previousCustomerId = this.personalData.id;
    tokenStorage.markSignedOut();
    runInAction(() => {
      this.retainedCartOwnerId = cause === 'user' ? null : previousCustomerId;
      this.isAuthorized = false;
      this.token = '';
      this.refreshToken = '';
      this.tokensSavedAt = null;
      this.isPersonalDataLoading = false;
      this.personalData.id = null;
      this.personalData.firstName = null;
      this.personalData.middleName = null;
      this.personalData.lastName = null;
      this.personalData.phone = null;
      this.personalData.email = null;
      this.personalData.flat = '';
      this.personalData.dateOfBirth = null;
      this.personalData.lastLogin = null;
      this.personalData.isAdult = false;
      this.personalData.isRateApp = false;
      this.personalData.type = 'User';
      this.personalData.freeDeliveryDaysLeft = 0;
      this.personalData.kycStatus = KYCStatus.NotVerified;
      this.personalData.verificationStatus = null;
      this.personalData.identificationNumber = null;
      this.addressList = [];
      if (this.deliveryAddress) {
        this.deliveryAddress.comment = '';
        this.deliveryAddress.instructions = [];
      }
      this.setBonuses(0);
      // Account-scoped and in-memory only, so nothing else clears it. It used
      // to be unreachable after a logout because the basket went with it and
      // `calculateCart` returns early on an empty one; a preserved basket makes
      // it reachable, and a signed-out `calculate` would otherwise still ask to
      // spend the points of an account it is no longer carrying.
      this.loyaltyPointsStore.setUseLoyaltyPoints(false);
      Object.keys(this.personalData.subscriptionFlags).forEach((flagName) => {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        this.personalData.subscriptionFlags[flagName].email = false;
      });
      orderStore.setIsFirstOrder(true);
      orderStore.setOrderStatus('none');
      orderStore.setActiveOrderID('');
      orderStore.setPaymentCards([]);
      orderStore.setActiveGift(null);
      orderStore.setGift(null);
      orderStore.clearOrderList();
      orderStore.setOrdersPollingEnabled(false);
      cartStore.promotionStore.resetPromocode();
      cartStore.promotionStore.proposedPromocodes.clear();
      catalogStore.favorites = {};
      if (cause === 'user') {
        cartStore.cart = [];
      }
      cartStore.isMissingBirthdayErrorCatched = false;
      mainStore.resetAnalytics();
      checkoutStore.setPayments([]);
      favoritesStore.reset();
      this.mindboxLoyaltyStore.resetCard();
      membershipStore.reset();
      membershipPurchaseStore.reset();
      membershipManageStore.reset();
      membershipCardChangeStore.reset();
    });
    refreshTokens.reset();
    Sentry.setUser({ ip_address: '{{auto}}' });
    analyticsService.setUserId(null);
    mainStore.sendToRN('logout');
    mainStore.sendToRN('setUserProperties', {
      customerId: null,
    });
    clearStoredPromocode();
  }

  async deleteProfile() {
    if (!this.isAuthorized) {
      return Promise.reject();
    }

    try {
      await CustomerRequests.deleteProfile();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  geocodingByCoordinates(
    { lat, lng }: GeoCoordinates,
    options?: GeocodingOptions,
  ): Promise<DeliveryAddress | null> {
    return company.config.useGoogleApiForGeocoding
      ? this.geocodingByCoordinatesWithGoogleApi({ lat, lng })
      : this.geocodingByCoordinatesWithBackendApi({ lat, lng }, options);
  }

  async geocodingByCoordinatesWithBackendApi(
    { lat, lng }: GeoCoordinates,
    options?: GeocodingOptions,
  ): Promise<DeliveryAddress | null> {
    let geocodingResponse: ReverseGeocodeResponse | null = null;
    try {
      ({ data: geocodingResponse } = await GeocoderRequests.reverseGeocode({
        lat,
        lng,
      }));
    } catch {
      mainStore.pushAlert('error', t('phrases:determineAddress'));

      return null;
    }

    if (!geocodingResponse) {
      return null;
    }

    const {
      AddNum,
      PostalExt,
      Postal,
      City,
      LongLabel,
      ShortLabel,
      Region,
      CountryCode,
    } = geocodingResponse;

    return {
      zip: PostalExt || Postal || '000000',
      country: CountryCode,
      city: City,
      region: Region ?? CountryCode,
      address1: LongLabel,
      address2: '',
      addressFloor: '',
      addressBld: AddNum || options?.buildingFallback || '',
      addressApt: '',
      shortAddress: ShortLabel,
      coordinates: { lat, lng },
      // FIXME: find out why do we use placeId
      placeId: mainStore.getPseudoId(),
      addressId: null,
      comment: '',
      instructions: [],
      // FIXME home/custom
      type: 'home',
    };
  }

  geocodingByCoordinatesWithGoogleApi(
    mapCenter: GeoCoordinates,
  ): Promise<DeliveryAddress | null> {
    return GoogleMapGeocode.geocodingByCoordinates({
      latlng: `${mapCenter.lat},${mapCenter.lng}`,
    })
      .then((e) => {
        let deliveryAddress: DeliveryAddress | null = null;
        if (e.status === 'OK') {
          if (e.results.length) {
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            const country = e.results[0].address_components.reduce(
              (str, item) =>
                str
                  ? str
                  : item.types.includes('country')
                    ? item.short_name
                    : '',
              '',
            );
            const addressComponentType: AddressComponentType =
              country === 'GB' ? 'postal_code' : 'plus_code';
            e.results = e.results
              .slice(0, 6)
              .filter((item) =>
                item.address_components.some((el) =>
                  el.types.includes(addressComponentType),
                ),
              );
            if (!e.results.length) {
              return Promise.resolve(deliveryAddress);
            }
            let index = 0;
            if (e.results.length > 1) {
              const pointsStringify: string[] = [];
              const points = e.results.map((item) => {
                const point = {
                  latitude: item.geometry.location.lat,
                  longitude: item.geometry.location.lng,
                };
                pointsStringify.push(JSON.stringify(point));
                return point;
              });
              index = pointsStringify.indexOf(
                JSON.stringify(
                  findNearest(
                    {
                      latitude: mapCenter.lat,
                      longitude: mapCenter.lng,
                    },
                    points,
                  ),
                ),
              );
            }
            const addressComponents: AddressComponents = {};
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            e.results[index].address_components.forEach((item) => {
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              addressComponents[item.types[0]] = {
                long_name: item.long_name,
                short_name: item.short_name,
              };
            });
            const address1 = this.getShortAddress(addressComponents);
            const shortAddress =
              (addressComponents.postal_code?.long_name ||
                addressComponents.plus_code?.long_name) &&
              address1
                ? `${address1}, ${
                    addressComponents.postal_code?.long_name ||
                    addressComponents.plus_code?.long_name
                  }`
                : address1
                  ? address1
                  : addressComponents.postal_code?.long_name ||
                    addressComponents.plus_code?.long_name ||
                    '';
            deliveryAddress = {
              zip:
                addressComponents.postal_code?.long_name ||
                addressComponents.plus_code?.long_name ||
                '000000',
              country: addressComponents.country?.short_name || '',
              city:
                addressComponents.postal_town?.long_name ||
                addressComponents.locality?.long_name ||
                addressComponents.administrative_area_level_1?.long_name ||
                addressComponents.country?.long_name ||
                '',
              region:
                addressComponents.administrative_area_level_2?.long_name ||
                addressComponents.country?.long_name ||
                '',
              address1: shortAddress,
              address2: '',
              addressFloor: '',
              addressBld: addressComponents.street_number?.long_name || '',
              addressApt: '',
              shortAddress,
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              coordinates: e.results[index].geometry.location,
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              placeId: e.results[index].place_id,
              addressId: null,
              comment: '',
              instructions: [],
              // FIXME home/custom
              type: 'home',
            };
          }
        }
        return Promise.resolve(deliveryAddress);
      })
      .catch((error) => {
        error && console.error(error);
        return null;
      });
  }

  async getAutocomplete(
    query: AutocompleteRequest,
  ): Promise<AutocompleteResponse> {
    try {
      return await DeliveryRequests.autocomplete(query);
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'getAutocomplete',
      );
    }
    return {
      data: {
        result: null,
        predictions: [],
      },
    };
  }

  getDeliveryZones(): Promise<DeliveryZoneCoverage[]> {
    return orderStore.deliveryStore.requestZones();
  }

  async fetchAddresses(): Promise<void> {
    this.deliveryAddressMeta.setLoading(true);

    try {
      const { data } = await DeliveryRequests.getAddresses();
      this.setAddressList(
        data.map((address) => ({
          ...address,
          building: address.building ?? address.street_address_2,
        })) || [],
      );
    } catch (e) {
      this.setAddressList([]);
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'fetchAddresses',
      );
    } finally {
      this.deliveryAddressMeta.setLoading(false);
    }
  }

  async addAddress(address: AddAddressRequest): Promise<Address | null> {
    try {
      const { data } = await DeliveryRequests.addAddress(address);
      return data;
    } catch {
      return null;
    }
  }

  async updateAddress(
    id: string,
    data: UpdateAddressRequest,
  ): Promise<AddressResponse | null> {
    try {
      return await DeliveryRequests.updateAddress(id, data);
    } catch {
      return null;
    }
  }

  async deleteAddress(id: string): Promise<boolean> {
    try {
      await DeliveryRequests.deleteAddress(id);
      await this.fetchAddresses();
      return true;
    } catch {
      return false;
    }
  }

  requestKYCSession(redirectUrl?: string) {
    if (!this.isAuthorized) {
      return Promise.reject();
    }

    analyticsService.track(AnalyticsEvent.KYC_VERIFICATION_STATUS, {
      status:
        this.personalData.kycStatus === KYCStatus.ResubmissionRequested
          ? KYCStatus.ResubmissionRequested
          : KYCStatus.Started,
    });

    return CustomerRequests.requestKYCSession({
      redirectUrl,
    }).catch((error) => {
      // All five callers swallow this rejection and navigate nowhere, so a
      // failure here is a customer tapping "verify" and watching nothing
      // happen — no error, no toast, no way past the verification gate to the
      // checkout. Reported at the source rather than at the call sites so a
      // sixth caller cannot forget. The rejection is re-thrown, so nothing
      // about how they behave changes.
      reportError(
        DOMAIN.auth,
        error ?? new Error('KYC session request rejected with no reason'),
        {
          reason: REASON.auth.kycSessionFailed,
          extras: {
            kycStatus: this.personalData.kycStatus,
            hasRedirectUrl: Boolean(redirectUrl),
          },
        },
      );
      throw error;
    });
  }

  // TODO: 17.10.2023 Move all referral program handlers to promotion store
  async fetchReferralProgramInfo() {
    if (!this.personalData.id) {
      return;
    }

    try {
      this.refereeInfo = await ReferralProgramRequests.getCustomerRefereeCode(
        this.personalData.id,
      );
      this.referralInfo = await ReferralProgramRequests.getReferralInfo();
    } catch (error: unknown) {
      await this.errorHandler(error as AxiosError<ApiErrorResponse>, '');
    }
  }

  clearReferralProgramInfo() {
    this.refereeInfo = null;
    this.referralInfo = null;
  }

  resetStore() {
    tokenStorage.markSignedOut();
    this.isFirstLaunch = true;
    this.isAuthorized = false;
    this.deliveryAddress = null;
    this.token = '';
    this.refreshToken = '';
    this.tokensSavedAt = null;
    this.retainedCartOwnerId = null;
    this.lang = getCookieLang();
    this.dir = 'ltr';

    // A unique token to manage address search sessions.
    // Reset the token after an address has been found.
    this.addressSearchSessionToken = undefined;

    this.draftUserData = null;

    this.isPersonalDataLoading = false;
    this.personalData = {
      id: null,
      firstName: null,
      middleName: null,
      lastName: null,
      phone: null,
      flat: '',
      dateOfBirth: null,
      email: null,
      lastLogin: null,
      subscriptionFlags: {},
      subscriptionId: '',
      isAdult: false,
      isRateApp: false,
      type: 'User',
      freeDeliveryDaysLeft: 0,
      kycStatus: KYCStatus.NotVerified,
      verificationStatus: null,
      identificationNumber: null,
    };
    this.playerId = '';
    this.sessionList = [];
    this.addressList = [];
    this.bonuses = null;
    this.bonusesTransactionsList = [];
    this.bonusesTransactionsPagination = {
      total: 0,
      page: 1,
    };
    this.addressToDelete = null;
    this.refereeInfo = null;
    this.referralInfo = null;
    this.deliveryZonesCoverages = [];
    this.deliveryZoneCacheTime = 0;
  }

  // Errors
  errorHandler = (
    error: AxiosError<ApiErrorResponse>,
    context: string,
  ): Promise<AxiosError> => mainStore.errorHandler(error, context);
}
